<template>
  <modal
    elem-id="modal_cliente_vincula"
    :show="isVisible"
    :title="t('Relacionar Cliente')"
    :close-label="t('Fechar')"
    :save-label="t('Novo Cliente')"
    save-icon="pi pi-plus"
    :save-fn="handleNewCliente"
    size="xlarge"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <p v-if="phone" style="margin: 0 0 0.75rem; color: #6b7280; font-size: 0.875rem;">
        {{ t('Relacionando') }}: {{ phone }}
      </p>
      <data-table
        ref="dataTableRef"
        :config="tableConfig"
        :endpoint="endpoint"
        :filterConfig="filterConfig"
        @edit-click="handleEditClick"
        @button-click="handleButtonClick">
      </data-table>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useMessagingChatCliente } from '../../composables/useMessagingChatCliente';

type ClienteId = number | string;

interface VinculaOpenPayload {
  phone?: string;
  deviceId?: string | number | null;
  whatsappfranquiaId?: string | number | null;
  contactName?: string;
}

interface CellParams {
  data: Record<string, unknown> & { _isFrozenFooter?: boolean };
  value?: unknown;
}

const { t } = useI18n();
const { linkChatCliente } = useMessagingChatCliente();

const isVisible = ref(false);
const phone = ref('');
const whatsappfranquiaId = ref<string | number | null>(null);
const contactName = ref('');
const dataTableRef = ref<unknown>(null);

const endpoint = computed(() => '/index.php/clientes/carrega_clientes_todos');

const tableConfig = computed(() => ({
  tableId: 'tabelaClientesVincula',
  title: '',
  columns: tableColumns.value,
  heightMode: 'fixed',
  height: 450,
  pagination: true,
  pageLength: 100,
}));

const filterConfig = computed(() => ({
  columns: {
    'nome': 'text',
    'franquia': 'select',
    'telefone_completo': 'text',
    'endereco_completo': 'text',
    'status': 'select',
  }
}));

const tableColumns = computed(() => [
  {
    data: 'cliente_id',
    title: 'ID',
    width: 80,
    resizable: true,
    sortable: true,
    renderType: 'clickable_id',
    dataType: 'number',
  },
  {
    data: 'nome',
    title: t('Nome'),
    width: 200,
    resizable: true,
    sortable: true,
    dataType: 'text',
  },
  {
    data: 'franquia',
    title: t('Franquia'),
    width: 150,
    resizable: true,
    sortable: true,
    dataType: 'text',
  },
  {
    data: 'telefone_completo',
    title: t('Telefone'),
    width: 160,
    resizable: true,
    sortable: true,
    dataType: 'text',
    cellRenderer: (params: CellParams) => {
      if (params.data._isFrozenFooter) return (params.value as string) || '';
      const ddi = String(params.data.ddi || '').replace(/\D/g, '');
      const ddd = String(params.data.ddd || '').replace(/\D/g, '');
      const tel = String(params.data.telefone || '').replace(/\D/g, '');
      const parts = [ddi, ddd, tel].filter(Boolean);
      return parts.join('') || '';
    }
  },
  {
    data: 'endereco_completo',
    title: t('Endereço'),
    width: 250,
    resizable: true,
    sortable: true,
    dataType: 'text',
  },
  {
    data: 'status',
    title: t('Status'),
    width: 100,
    resizable: true,
    sortable: true,
    dataType: 'number',
    cellRenderer: (params: CellParams) => {
      if (params.data._isFrozenFooter) return '';
      const status = params.data.status;
      if (status === 0) {
        return '<span class="status-badge status-inactive">' + t('Inativo') + '</span>';
      }
      return '<span class="status-badge status-active">' + t('Ativo') + '</span>';
    }
  },
  {
    data: 'cliente_id',
    title: t('Vincular'),
    width: 100,
    resizable: false,
    sortable: false,
    renderType: 'clickable_button',
    buttonClass: 'p-button-success p-button-sm',
    buttonIcon: 'pi pi-link',
    buttonText: t('Vincular'),
    buttonAction: 'vincular',
  },
]);

const handleEditClick = (clienteId: ClienteId): void => {
  log('ClienteVinculaModal', 'handleEditClick', 'Opening cliente modal', clienteId);
  eventBus.emit('modal.cliente.open', { id: clienteId });
};

const handleButtonClick = async (
  _value: unknown,
  data: { cliente_id: ClienteId },
  action: string
): Promise<void> => {
  if (action !== 'vincular') return;

  log('ClienteVinculaModal', 'handleButtonClick', 'Linking cliente', { clienteId: data.cliente_id, phone: phone.value });

  try {
    await linkChatCliente(phone.value, Number(data.cliente_id), 'manual', whatsappfranquiaId.value as null);
    eventBus.emit('globalMessage.success', t('Cliente vinculado com sucesso'));
    isVisible.value = false;
  } catch (err) {
    log('ClienteVinculaModal', 'handleButtonClick', 'Error linking cliente', err, 'error');
    eventBus.emit('globalMessage.error', t('Erro ao vincular cliente'));
  }
};

const handleNewCliente = (): void => {
  log('ClienteVinculaModal', 'handleNewCliente', 'Opening new cliente modal');
  eventBus.emit('modal.cliente.open', {
    prefillPhone: phone.value,
    prefillName: contactName.value || '',
  });
};

const handleClose = (): void => {
  log('ClienteVinculaModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
};

const handleOpen = (data?: VinculaOpenPayload): void => {
  log('ClienteVinculaModal', 'handleOpen', 'Opening with data', data);
  phone.value = data?.phone || '';
  whatsappfranquiaId.value = data?.deviceId || data?.whatsappfranquiaId || null;
  contactName.value = data?.contactName || '';
  isVisible.value = true;
};

onMounted(() => {
  eventBus.on('modal.vincula-cliente.open', handleOpen);
  eventBus.on('modal.vincula-cliente.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.vincula-cliente.open', handleOpen);
  eventBus.off('modal.vincula-cliente.close', handleClose);
});
</script>

<style scoped>
:deep(.status-badge) {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
}

:deep(.status-active) {
  background-color: #d4edda;
  color: #155724;
}

:deep(.status-inactive) {
  background-color: #f8d7da;
  color: #721c24;
}
</style>
